import React from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LineChart,
  ListCard,
  ListCardRow,
  LoadingOrbit,
  Page,
  PageHeader,
  Progress,
  Text,
} from '@chatsdet/ui';
import { Bug, CalendarBlank, FlowArrow, Warning, Wrench } from '@phosphor-icons/react';
import { digestDayLabel, formatDigestRate } from '@chatsdet/types';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDigest } from '../../features/digests/useDigests';
import { DIGEST_KIND_LABEL, digestWindowLabel, digestWindowRange } from '../../features/digests/format';
import { toUrlId } from '../../lib/prefixed-ids';
import { API_URL } from '../../config';
import { buildDigestOverview } from '../../features/digests/overview';
import { DigestHeadlinePanel } from '../components/digests/DigestHeadlinePanel';
import { DigestSectionHeading } from '../components/digests/DigestSectionHeading';
import { DigestSinceStats } from '../components/digests/DigestSinceStats';
import { issueSeverityBadgeVariants, formatSeverityLabel } from '../components/issues/issueBadges';
import { AgentPromptButton } from '../components/agent-prompts/AgentPromptButton';

export function DigestPage() {
  const navigate = useNavigate();
  const { digestId } = useParams({ from: '/digests/$digestId' });
  const { user } = useAuth();
  const digestQuery = useDigest(digestId);
  const digest = digestQuery.data;
  const orgName = user?.organizations?.find((org) => org.id === user.orgId)?.name ?? 'your organization';
  const windowLabel = digest
    ? digestWindowLabel(digest.kind, digest.windowStart, digest.windowEnd, digest.factsJson.window.timezone)
    : 'Digest';

  useBreadcrumbs([
    { label: 'Home', onPress: () => navigate({ to: '/' }) },
    { label: windowLabel },
  ]);

  if (digestQuery.isLoading) {
    return (
      <Page>
        <Flex align="center" justify="center" className="min-h-[400px]">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }

  if (digestQuery.isError || !digest) {
    return (
      <Page>
        <PageHeader icon={<CalendarBlank size={24} />} title="Digest not found" />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This digest may have been deleted or you may not have access.
          </Text>
          <Button variant="outline" size="sm" onPress={() => navigate({ to: '/' })} className="mt-4">
            Back to Home
          </Button>
        </div>
      </Page>
    );
  }

  const facts = digest.factsJson;
  const overview = buildDigestOverview(facts, digest.kind);
  const issueItems = facts.issues.items;
  const flowItems = facts.flows.items;
  const maintainedItems = facts.maintained.items;
  const extraIssues = facts.issues.filed - issueItems.length;
  const extraFlows = facts.flows.promoted - flowItems.length;
  const attentionItems = facts.needsAttention.items;
  const extraAttention = facts.needsAttention.count - attentionItems.length;
  const extraMaintained = facts.maintained.count - maintainedItems.length;

  return (
    <Page>
      <PageHeader
        icon={<CalendarBlank size={24} />}
        title={windowLabel}
        description={`${orgName} · ${digestWindowRange(facts.window.start, facts.window.end, facts.window.timezone)}`}
        actions={
          <Flex align="center" gap="sm">
            <Badge variant={digest.kind === 'daily' ? 'default' : 'secondary'}>{DIGEST_KIND_LABEL[digest.kind]}</Badge>
            {!digest.quiet && (
              <AgentPromptButton promptUrl={`${API_URL}/digests/${digest.id}/agent-prompt`} />
            )}
          </Flex>
        }
      />
      {digest.quiet ? (
        <div className="mt-6 max-w-3xl">
          <EmptyState
            icon={<CalendarBlank size={24} />}
            title={digest.kind === 'daily' ? 'Quiet day' : 'Quiet week'}
            description="No runs, no Issues, no promoted Workflows, and no Cofactor maintenance in this window."
          />
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {digest.summaryMd ? (
            <Text size="lg" className="max-w-4xl text-foreground">
              {digest.summaryMd}
            </Text>
          ) : (
            <Text size="sm" className="text-muted-foreground italic">
              Summary is still generating.
            </Text>
          )}

          {overview.hasCapabilities && (
            <section className="space-y-4">
              <DigestSectionHeading accent={`${overview.current}.`}>Capabilities and coverage,</DigestSectionHeading>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <DigestHeadlinePanel panel={overview.capabilities} />
                <DigestHeadlinePanel panel={overview.coverage} />
              </div>
            </section>
          )}

          <section className="space-y-4">
            <DigestSectionHeading accent={`${overview.previousWord}.`}>Since</DigestSectionHeading>
            <DigestSinceStats boxes={overview.boxes} />
          </section>

          <DigestSectionHeading>In detail.</DigestSectionHeading>

          <Card variant="bordered" padding="md">
            <CardHeader>
              <Heading level={3}>Product map by area</Heading>
            </CardHeader>
            <CardContent>
              {facts.areas.length === 0 ? (
                <EmptyState size="sm" title="No product map yet" />
              ) : (
                <div className="space-y-4">
                  {facts.areas.map((area) => (
                    <div key={area.areaId ?? 'unassigned'} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <div className="sm:w-48 sm:shrink-0">
                        <div className="flex items-center gap-2">
                          {area.areaId ? (
                            <Link
                              to="/product-map"
                              search={{ area: toUrlId('parea', area.areaId) }}
                              className="truncate text-sm font-medium hover:underline"
                              title={area.title}
                            >
                              {area.title}
                            </Link>
                          ) : (
                            <Text size="sm" className="truncate font-medium" title={area.title}>
                              {area.title}
                            </Text>
                          )}
                          {area.isNew && <Badge variant="secondary">New</Badge>}
                        </div>
                        {!area.isNew && area.capabilitiesAdded > 0 && (
                          <Text size="xs" className="text-muted-foreground">
                            +{area.capabilitiesAdded} capabilities
                          </Text>
                        )}
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <div className="flex items-center gap-3">
                          <Progress
                            value={area.coverage ?? 0}
                            size="sm"
                            variant="default"
                            aria-label={`${area.title} coverage`}
                            className="flex-1"
                          />
                          <Text size="xs" className="w-36 shrink-0 text-right tabular-nums text-muted-foreground sm:w-40">
                            {formatDigestRate(area.coverage)} covered · {area.mapped} of {area.capabilities}
                          </Text>
                        </div>
                        <div className="flex items-center gap-3">
                          <Progress
                            value={area.passRate ?? 0}
                            size="sm"
                            variant="success"
                            aria-label={`${area.title} passing`}
                            className="flex-1"
                          />
                          <Text size="xs" className="w-36 shrink-0 text-right tabular-nums text-muted-foreground sm:w-40">
                            {formatDigestRate(area.passRate)} passing · {area.passing} of {area.mapped}
                          </Text>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center gap-4 pt-1">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-3 rounded-full bg-primary" />
                      <Text size="xs" className="text-muted-foreground">
                        Covered
                      </Text>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-3 rounded-full bg-success" />
                      <Text size="xs" className="text-muted-foreground">
                        Passing
                      </Text>
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            <Card variant="bordered" padding="md">
              <CardHeader>
                <Heading level={3}>Coverage and pass rate, last 7 days</Heading>
              </CardHeader>
              <CardContent>
                <LineChart
                  data={facts.series.map((point) => ({
                    day: digestDayLabel(point.date),
                    coverage: point.coverage,
                    passRate: point.passRate,
                  }))}
                  xDataKey="day"
                  lines={[
                    { dataKey: 'coverage', name: 'Coverage' },
                    { dataKey: 'passRate', name: 'Pass rate' },
                  ]}
                  showLegend
                  connectNulls
                  height={220}
                />
              </CardContent>
            </Card>

            <div className="space-y-6">
              <ListCard title="Issues filed" description={`${facts.issues.filed} filed · ${facts.issues.open} open`}>
                {issueItems.length === 0 ? (
                  <div className="p-4">
                    <EmptyState size="sm" title="No Issues filed" />
                  </div>
                ) : (
                  <>
                    {issueItems.map((ref, index) => (
                      <ListCardRow
                        key={ref.issueId}
                        index={index}
                        icon={<Bug size={16} />}
                        title={
                          <Link to="/issues" search={{ issueId: ref.issueId }} className="hover:underline" title={ref.title}>
                            {ref.title}
                          </Link>
                        }
                        badges={
                          <Badge variant={issueSeverityBadgeVariants[ref.severity]}>
                            {formatSeverityLabel(ref.severity)}
                          </Badge>
                        }
                        meta={<Text size="xs">{ref.filedBy === 'agent' ? 'Filed by agent' : 'Filed manually'}</Text>}
                      />
                    ))}
                    {extraIssues > 0 && (
                      <div className="px-4 py-2">
                        <Text size="xs" className="text-muted-foreground">
                          …and {extraIssues} more
                        </Text>
                      </div>
                    )}
                  </>
                )}
              </ListCard>

              <ListCard title="New Workflows" description={`${facts.flows.promoted} promoted to approved`}>
                {flowItems.length === 0 ? (
                  <div className="p-4">
                    <EmptyState size="sm" title="No new Workflows" />
                  </div>
                ) : (
                  <>
                    {flowItems.map((flow, index) => (
                      <ListCardRow
                        key={flow.workflowId}
                        index={index}
                        icon={<FlowArrow size={16} />}
                        title={
                          <Link
                            to="/flows/$workflowId"
                            params={{ workflowId: toUrlId('wf', flow.workflowId) }}
                            className="hover:underline"
                            title={flow.name}
                          >
                            {flow.name}
                          </Link>
                        }
                      />
                    ))}
                    {extraFlows > 0 && (
                      <div className="px-4 py-2">
                        <Text size="xs" className="text-muted-foreground">
                          …and {extraFlows} more
                        </Text>
                      </div>
                    )}
                  </>
                )}
              </ListCard>

              {facts.needsAttention.count > 0 && (
                <div data-testid="digest-needs-attention">
                  <ListCard title="Needs your attention" description={`${facts.needsAttention.count} Workflow${facts.needsAttention.count === 1 ? '' : 's'}`}>
                    {attentionItems.map((flow, index) => (
                      <ListCardRow
                        key={flow.workflowId}
                        index={index}
                        icon={<Warning size={16} />}
                        title={
                          <Link
                            to="/flows/$workflowId"
                            params={{ workflowId: toUrlId('wf', flow.workflowId) }}
                            className="hover:underline"
                            title={flow.name}
                          >
                            {flow.name}
                          </Link>
                        }
                        meta={<span>{flow.reasonLabel}</span>}
                        footer={
                          flow.remediation ? (
                            <Text size="xs" className="text-muted-foreground text-pretty mt-1">
                              {flow.remediation}
                            </Text>
                          ) : undefined
                        }
                      />
                    ))}
                    {extraAttention > 0 && (
                      <div className="px-4 py-2">
                        <Text size="xs" className="text-muted-foreground">
                          …and {extraAttention} more
                        </Text>
                      </div>
                    )}
                  </ListCard>
                </div>
              )}

              <ListCard title="Maintained by Cofactor" description={`${facts.maintained.count} maintained`}>
                {maintainedItems.length === 0 ? (
                  <div className="p-4">
                    <EmptyState size="sm" title="Nothing maintained" />
                  </div>
                ) : (
                  <>
                    {maintainedItems.map((flow, index) => (
                      <ListCardRow
                        key={flow.workflowId}
                        index={index}
                        icon={<Wrench size={16} />}
                        title={
                          <Link
                            to="/flows/$workflowId"
                            params={{ workflowId: toUrlId('wf', flow.workflowId) }}
                            className="hover:underline"
                            title={flow.name}
                          >
                            {flow.name}
                          </Link>
                        }
                        footer={
                          <Text size="xs" className="text-muted-foreground text-pretty mt-1">
                            {flow.changeSummary}
                          </Text>
                        }
                      />
                    ))}
                    {extraMaintained > 0 && (
                      <div className="px-4 py-2">
                        <Text size="xs" className="text-muted-foreground">
                          …and {extraMaintained} more
                        </Text>
                      </div>
                    )}
                  </>
                )}
              </ListCard>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
